import type { CryptoAsset } from '../../types'
import { formatMYR } from '../../lib/utils'
import { cx, text, focus } from '../../lib/ui-primitives'

// ============================================
// CRYPTO ASSET CARD
// Selectable crypto asset display
// ============================================

export interface CryptoAssetCardProps {
  /** Crypto asset data */
  asset: CryptoAsset
  /** Current price in MYR */
  price?: number
  /** Called when card is clicked */
  onClick?: () => void
  /** Whether card is selected */
  selected?: boolean
  /** Disabled state */
  disabled?: boolean
}

/**
 * CryptoAssetCard - Selectable crypto display
 *
 * Extracted from: WelcomeScreen crypto list
 *
 * Features:
 * - Crypto icon, name, symbol
 * - Price display with loading state
 * - Network count badge
 * - Accessible button with proper labels
 * - Gradient background with crypto color
 */
export function CryptoAssetCard({
  asset,
  price,
  onClick,
  selected = false,
  disabled = false,
}: CryptoAssetCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cx(
        'w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl border',
        'transition-all duration-200',
        focus.ring,
        selected
          ? 'border-white/30 bg-white/[0.08]'
          : 'border-white/10 hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.05]',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      style={{
        background: `linear-gradient(135deg, ${asset.color}15, rgba(255,255,255,0.02) 55%, rgba(255,255,255,0.01))`,
      }}
      aria-label={`Buy ${asset.name} at ${price ? formatMYR(price) : 'loading price'}`}
      aria-pressed={selected}
    >
      <div className="flex items-center justify-between">
        {/* Left: Icon + Name */}
        <div className="flex items-center gap-2.5 sm:gap-3">
          <span className="text-xl sm:text-2xl" aria-hidden="true">
            {asset.icon}
          </span>
          <div className="text-left">
            <div className="text-white font-semibold text-sm tracking-tight">
              {asset.symbol}
            </div>
            <div className={cx('text-xs', text.tertiary)}>
              {asset.name}
            </div>
          </div>
        </div>

        {/* Right: Price + Networks */}
        <div className="text-right">
          <div className={cx('text-white text-sm', text.mono, text.numeric)}>
            {price ? formatMYR(price) : '—'}
          </div>
          <div className={cx('text-[10px]', text.disabled)}>
            {asset.networks.length} network{asset.networks.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </button>
  )
}

// ============================================
// CRYPTO ASSET PILL
// Compact crypto display (for headers)
// ============================================

export interface CryptoAssetPillProps {
  /** Crypto asset data */
  asset: CryptoAsset
  /** Network name */
  network?: string
}

/**
 * CryptoAssetPill - Compact crypto badge
 *
 * Extracted from: AmountScreen header pill
 */
export function CryptoAssetPill({ asset, network }: CryptoAssetPillProps) {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10"
      style={{ backgroundColor: `${asset.color}18` }}
    >
      <span aria-hidden="true">{asset.icon}</span>
      <span className="text-white font-medium">{asset.symbol}</span>
      {network && (
        <>
          <span className="text-white/30">•</span>
          <span className={cx('text-sm', text.secondary)}>{network}</span>
        </>
      )}
    </div>
  )
}
